import { TEXTOS } from '../../../lib/glossario'
import type { ParcelaDoTis, ParcelasDoTis as Parcelas } from '../../../types/chamado'

const HORAS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })

/**
 * O TIS aberto incidente a incidente.
 *
 * O TIS é o único indicador que não tem nota: ele **soma** um desconto por chamado corretivo que
 * passou do prazo. Um número assim só é conferível se a lista estiver à vista — do mesmo jeito que
 * os outros indicadores mostram o denominador ao lado do valor.
 *
 * **A soma bruta aparece sempre, mesmo quando o teto a corta.** "−10% na nota" pode ser uma soma de
 * 10 ou de 28 limitada pelo teto do indicador, e as duas coisas dizem coisas muito diferentes sobre
 * o mês para quem vai assinar.
 *
 * @param props as parcelas do indicador
 * @returns a lista dos incidentes medidos e o que ficou de fora
 */
export function ParcelasDoTis({ parcelas }: { parcelas: Parcelas }) {
  const t = TEXTOS.tis
  return (
    <div className="mt-3 space-y-3">
      {parcelas.tetoAlcancado && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-inset ring-amber-200">
          {t.tetoAlcancado(parcelas.somaBruta, parcelas.tetoDoIndicador)}
        </p>
      )}

      {parcelas.itens.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
          {parcelas.itens.map((item) => (
            <Incidente key={item.chamado} item={item} />
          ))}
        </ul>
      )}

      <Ausentes rotulo={t.semCriticidade} chamados={parcelas.corretivosSemCriticidade} />
      <Ausentes rotulo={t.semMedicao} chamados={parcelas.corretivosSemMedicao} />

      {parcelas.advertencias > 0 && <p className="text-xs text-slate-600">{t.advertencias(parcelas.advertencias)}</p>}
      {!parcelas.descontouSuspensoes && <p className="text-xs text-slate-600">{t.semDescontoDeSuspensoes}</p>}
    </div>
  )
}

/** Um chamado medido: o tempo, o degrau do contrato e o que ele custou. */
function Incidente({ item }: { item: ParcelaDoTis }) {
  const t = TEXTOS.tis
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-3 py-2 text-xs">
      <div className="min-w-0">
        <span className="font-medium tabular-nums text-slate-900">{item.chamado}</span>{' '}
        <span className="text-slate-700">{item.criticidadeRotulo}</span>{' '}
        {/* De onde veio a criticidade, ao lado dela. É ela que define o prazo e portanto o desconto;
            um desconto cuja origem não se vê é um desconto que se discute no processo. */}
        <span className="text-slate-500">({item.fonteRotulo})</span>
        {item.divergenciaComACategoria && (
          <span className="text-amber-800"> · {t.divergencia(item.divergenciaComACategoria)}</span>
        )}
        <div className="text-slate-600">
          {t.horasUteis(HORAS.format(item.horasUteisDecorridas))}
          {item.horasUteisDeAtraso > 0 && ` · ${t.atraso(HORAS.format(item.horasUteisDeAtraso))}`}
        </div>
        {/* A conta, e não só o resultado: é a diferença entre conferir e acreditar. */}
        {item.descontouSuspensoes && item.horasUteisEmEspera > 0 && (
          <div className="text-slate-500">
            {t.descontoDePendente(HORAS.format(item.horasUteisBrutas), HORAS.format(item.horasUteisEmEspera))}
          </div>
        )}
        {!item.descontouSuspensoes && <div className="text-amber-800">{t.semDescontoNesteChamado}</div>}
        <div className="text-slate-500">{item.degrauDoTr}</div>
      </div>
      <Consequencia item={item} />
    </li>
  )
}

/** O que o incidente custou. Advertência não é desconto, e não pode parecer um. */
function Consequencia({ item }: { item: ParcelaDoTis }) {
  if (item.consequencia === 'ADVERTENCIA') {
    return (
      <span className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-900">
        {TEXTOS.tis.advertencia}
      </span>
    )
  }
  if (item.redutorPercentual === 0) {
    return <span className="whitespace-nowrap text-slate-500">{TEXTOS.tis.noPrazo}</span>
  }
  return (
    <span className="whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-900">
      −{item.redutorPercentual}%
    </span>
  )
}

/** O que ficou de fora da soma. Nunca some da tela: fora da conta não é o mesmo que zero. */
function Ausentes({ rotulo, chamados }: { rotulo: (quantos: number) => string; chamados: string[] }) {
  if (chamados.length === 0) {
    return null
  }
  return (
    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
      {rotulo(chamados.length)} <span className="font-medium tabular-nums">{chamados.join(', ')}</span>
    </p>
  )
}
